/**
 * theme.js — Team Practice Gear
 *
 * Vanilla JS only. No dependencies.
 * Handles: mobile nav toggle, announcement bar dismiss,
 *          mobile nav focus trap, sticky header class.
 */

(function () {
  'use strict';

  /* ── Utility helpers ─────────────────────────────────────── */

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  function on(el, event, handler, options) {
    if (el) el.addEventListener(event, handler, options || false);
  }

  /* ── Announcement Bar ────────────────────────────────────── */
  /**
   * Reads the dismissal key from the bar's data attribute so each
   * unique message can be independently dismissed. Stores the key
   * in localStorage — if the user clears storage the bar reappears,
   * which is intentional for new messages.
   */
  function initAnnouncementBar() {
    var bar = qs('[data-announcement-bar]');
    if (!bar) return;

    var dismissKey = bar.dataset.dismissKey || 'tpg-announcement-dismissed';

    // Hide immediately if already dismissed
    if (localStorage.getItem(dismissKey) === '1') {
      bar.hidden = true;
      return;
    }

    // Show bar and update header offset CSS variable
    bar.hidden = false;
    updateAnnouncementOffset(bar);

    // Dismiss button
    var closeBtn = qs('[data-announcement-close]', bar);
    on(closeBtn, 'click', function () {
      bar.style.maxHeight = bar.scrollHeight + 'px';
      bar.addEventListener('transitionend', function handler() {
        bar.hidden = true;
        bar.removeEventListener('transitionend', handler);
        updateAnnouncementOffset(null);
      });
      bar.style.maxHeight = '0';
      bar.style.overflow = 'hidden';
      localStorage.setItem(dismissKey, '1');
    });

    // Recalculate offset on resize
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (!bar.hidden) updateAnnouncementOffset(bar);
      }, 100);
    });
  }

  function updateAnnouncementOffset(bar) {
    var height = bar ? bar.offsetHeight + 'px' : '0px';
    document.documentElement.style.setProperty('--announcement-bar-height', height);
  }

  /* ── Mobile Navigation ───────────────────────────────────── */
  function initMobileNav() {
    var toggle  = qs('[data-nav-toggle]');
    var nav     = qs('[data-nav-menu]');
    var overlay = qs('#nav-overlay');

    if (!toggle || !nav) return;

    function openNav() {
      nav.setAttribute('aria-hidden', 'false');
      nav.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      document.body.classList.add('nav-is-open');
      if (overlay) {
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
      }
      trapFocus(nav);
    }

    function closeNav() {
      nav.setAttribute('aria-hidden', 'true');
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      document.body.classList.remove('nav-is-open');
      if (overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
      }
      releaseFocus();
      toggle.focus();
    }

    on(toggle, 'click', function () {
      var isOpen = nav.classList.contains('is-open');
      isOpen ? closeNav() : openNav();
    });

    on(overlay, 'click', closeNav);

    // Close on Escape
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) {
        closeNav();
      }
    });

    // Dropdown toggles (mobile)
    qsa('[data-dropdown-toggle]', nav).forEach(function (btn) {
      on(btn, 'click', function () {
        var parent  = btn.closest('[data-has-dropdown]');
        var submenu = parent && qs('[data-dropdown-menu]', parent);
        if (!submenu) return;

        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        submenu.hidden = expanded;
      });
    });
  }

  /* ── Focus Trap ──────────────────────────────────────────── */
  var _focusTrapListener = null;

  function trapFocus(container) {
    var focusable = getFocusable(container);
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (first) first.focus();

    _focusTrapListener = function (e) {
      if (e.key !== 'Tab') return;
      if (focusable.length === 0) { e.preventDefault(); return; }

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', _focusTrapListener);
  }

  function releaseFocus() {
    var nav = qs('[data-nav-menu]');
    if (nav && _focusTrapListener) {
      nav.removeEventListener('keydown', _focusTrapListener);
      _focusTrapListener = null;
    }
  }

  function getFocusable(container) {
    return qsa(
      'a[href], button:not([disabled]), input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) {
      return !el.closest('[hidden]') && el.offsetParent !== null;
    });
  }

  /* ── Sticky Header ───────────────────────────────────────── */
  function initStickyHeader() {
    var header = qs('[data-site-header]');
    if (!header) return;

    var scrolled = false;

    function onScroll() {
      var shouldAdd = window.scrollY > 10;
      if (shouldAdd !== scrolled) {
        header.classList.toggle('site-header--scrolled', shouldAdd);
        scrolled = shouldAdd;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Smooth scroll for on-page anchors ──────────────────── */
  function initSmoothScroll() {
    on(document, 'click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var target = qs(link.getAttribute('href'));
      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    });
  }

  /* ── Init ────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    initAnnouncementBar();
    initMobileNav();
    initStickyHeader();
    initSmoothScroll();
  });

}());
